import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../contexts/LocalizationContext';
import api, { getApiImageUrl } from '../services/api';
import DragonSelectionScreen from './DragonSelectionScreen';
import './CardScreen.css';

const CardScreen = ({ currentPlayer }) => {
  const { moduleName, cardId } = useParams();
  const navigate = useNavigate();
  const { token, debugConfig } = useAuth();
  const { t, language, loading: locLoading } = useLocalization();
  
  // Debug localization
  console.log('CardScreen localization state:', { language, loading: locLoading });
  console.log('Sample translation test:', t('common.back'));
  
  // Helper function for localization with fallbacks
  const getTranslation = (key, fallback) => {
    const translation = t(key);
    if (translation === key || !translation) {
      console.warn(`Translation missing for key: ${key}, using fallback: ${fallback}`);
      return fallback;
    }
    return translation;
  };
  
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const [timeSpent, setTimeSpent] = useState(0);
  const [startTime] = useState(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [moduleId, setModuleId] = useState(null);
  const [showChoice, setShowChoice] = useState(false);
  const [currentChoice, setCurrentChoice] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (currentPlayer) {
      resolveModuleAndFetchCard();
    }
  // Re-fetch on language change so the card text follows the selected language
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleName, cardId, currentPlayer, language]);

  // Focus input when card loads and it's not a multiple choice question
  useEffect(() => {
    if (card && !loading && card.content.answerType !== 'multiple-choice') {
      // Small delay to ensure rendering is complete
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  }, [card, loading]);

  const resolveModuleAndFetchCard = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First resolve module name to ID
      const response = await api.get(`/api/module/resolve/${moduleName}`);
      const moduleData = response.data;
      setModuleId(moduleData.module_id);
      
      // Now fetch the card with the resolved module ID
      await fetchCard(moduleData.module_id);
      
    } catch (err) {
      console.error('Error resolving module:', err);
      setError(err.message);
      setLoading(false);
    }
  };


  useEffect(() => {
    // Update time spent every second
    const timer = setInterval(() => {
      setTimeSpent(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime]);

  useEffect(() => {
    // Focus the component when it loads to enable keyboard shortcuts
    const cardElement = document.querySelector('.card-screen');
    if (cardElement) {
      cardElement.focus();
    }
  }, [card]); // Focus when card loads

  const fetchCard = async (resolvedModuleId) => {
    if (!currentPlayer) {
      setError('No player selected');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      let url;
      if (cardId) {
        url = `/api/card/${resolvedModuleId}/${cardId}?player_id=${currentPlayer.id}&language=${language}`;
      } else {
        url = `/api/card/${resolvedModuleId}?player_id=${currentPlayer.id}&language=${language}`;
      }
      
      const response = await api.get(url);
      const cardData = response.data;
      
      // Check if response contains a choice instead of a card
      if (cardData.type === 'choice') {
        // Handle choice moment - redirect to choice handling
        handleChoiceMoment(cardData.choice);
        return;
      }
      
      // Check if module is completed
      if (cardData.completed) {
        // Show completion message and redirect to modules
        setError(cardData.message || getTranslation('cards.moduleCompleted', 'Congratulations! You have completed all cards in this module.'));
        setTimeout(() => {
          navigate('/play');
        }, 3000);
        return;
      }
      
      setCard(cardData);
      
      // Reset answer states for new card
      setUserAnswer('');
      setSelectedOption('');
      
    } catch (err) {
      console.error('Error fetching card:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChoiceMoment = (choice) => {
    console.log('Choice moment detected:', choice);
    setCurrentChoice(choice);
    setShowChoice(true);
    setLoading(false);
  };

  const resolveChoice = async (choiceType, resolutionData) => {
    try {
      setSubmitting(true);
      
      // Add player_id to resolution data for security validation
      const dataWithPlayer = {
        ...resolutionData,
        player_id: currentPlayer?.id
      };
      
      const response = await api.post(`/api/choices/resolve/${choiceType}`, dataWithPlayer);

      // Choice resolved successfully - hide choice UI and reload card
      setShowChoice(false);
      setCurrentChoice(null);
      
      // Reload the card now that choice is resolved
      await fetchCard(moduleId);
      
    } catch (err) {
      console.error('Error resolving choice:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (submitting) return;
    
    try {
      setSubmitting(true);
      
      let answerToSubmit = userAnswer;
      if (card.content.answerType === 'multiple_choice') {
        answerToSubmit = selectedOption;
      }
      
      // For lessons, just mark as completed
      if (card.type === 'lesson') {
        answerToSubmit = 'lesson_completed';
      }
      
      const response = await api.post(`/api/submit/answer/${card.id}`, {
        userAnswer: answerToSubmit,
        timeSpent,
        moduleId,
        language: language,
        playerId: currentPlayer?.id
      });

      const result = response.data;
      
      // Navigate to result screen with the result data
      navigate('/result', { 
        state: { 
          result, 
          moduleId,
          moduleName,
          currentCardId: card.id,
          cardData: card  // Pass card data for incorrect answer display
        } 
      });
      
    } catch (err) {
      console.error('Error submitting answer:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDebugAnswer = async () => {
    // Safety check: Is debug enabled in config?
    if (!debugConfig?.debug_fast_answers) {
      return; // Silent ignore
    }
    
    if (submitting) return;
    
    console.log('Debug answer activated');
    
    try {
      setSubmitting(true);
      
      const response = await api.post(`/api/submit/answer/${card.id}`, {
        userAnswer: 'DEBUG_CORRECT_ANSWER',
        timeSpent,
        moduleId,
        language: language,
        playerId: currentPlayer?.id
      });

      const result = response.data;
      
      // Navigate to result screen with the result data
      navigate('/result', { 
        state: { 
          result, 
          moduleId,
          moduleName,
          currentCardId: card.id,
          cardData: card  // Pass card data for incorrect answer display
        } 
      });
      
    } catch (err) {
      console.error('Error submitting debug answer:', err);
      setError(err.message);
      setSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    // Handle debug shortcut: Ctrl+Shift+D
    if (e.ctrlKey && e.shiftKey && e.key === 'D') {
      e.preventDefault();
      handleDebugAnswer();
      return;
    }
    
    // Handle keyboard shortcuts for multiple choice
    if (card?.content.answerType === 'multiple_choice') {
      const key = e.key.toUpperCase();
      console.log('Key pressed:', key); // Debug log
      const option = card.content.options?.find(opt => opt.key === key);
      console.log('Found option:', option); // Debug log
      if (option) {
        e.preventDefault(); // Prevent default browser behavior
        handleMultipleChoiceSelection(key);
      }
    }
  };

  const handleMultipleChoiceSelection = async (optionKey) => {
    if (submitting) return;
    
    setSelectedOption(optionKey);
    
    // Submit immediately after selection
    try {
      setSubmitting(true);
      
      const response = await api.post(`/api/submit/answer/${card.id}`, {
        userAnswer: optionKey,
        timeSpent,
        moduleId,
        language: language,
        playerId: currentPlayer?.id
      });

      const result = response.data;
      
      // Navigate to result screen with the result data
      navigate('/result', { 
        state: { 
          result, 
          moduleId,
          moduleName,
          currentCardId: card.id,
          cardData: card  // Pass card data for incorrect answer display
        } 
      });
      
    } catch (err) {
      console.error('Error submitting answer:', err);
      setError(err.message);
      setSubmitting(false);
    }
  };

  const isAnswerValid = () => {
    if (!card) return false;
    
    if (card.type === 'lesson') return true;
    
    if (card.content.answerType === 'multiple_choice') {
      return selectedOption !== '';
    }
    
    if (card.content.answerType === 'numerical' || card.content.answerType === 'text') {
      return userAnswer.trim() !== '';
    }
    
    return false;
  };

  if (loading) {
    return (
      <div className="card-screen">
        <div className="loading">
          <div className="spinner"></div>
          <p>{getTranslation('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-screen">
        <div className="error">
          <h2>{getTranslation('common.error', 'Error')}</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Show choice modal if a choice is pending
  if (showChoice && currentChoice) {
    // For now, handle dragon-selection choice specifically
    if (currentChoice.choice_type === 'dragon-selection') {
      return (
        <div className="card-screen">
          <div className="choice-modal-overlay">
            <div className="choice-modal">
              <DragonSelectionScreen 
                onDragonSelected={(dragonType) => {
                  resolveChoice('dragon-selection', { dragon_type: dragonType });
                }}
              />
            </div>
          </div>
        </div>
      );
    }
    // Add more choice types here as needed
  }

  if (!card) {
    return (
      <div className="card-screen">
        <div className="error">
          <h2>{getTranslation('cards.noCard', 'Card not found')}</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="card-screen" onKeyDown={handleKeyPress} tabIndex="0">
      <div className="card-header">
        <div className="card-meta">
          <span className="card-points">{card.points} ⭐</span>
        </div>
      </div>

      <div className="card-content">
        {/* Display images at the top */}
        {card.content.images && card.content.images.length > 0 && (
          <div className="card-images">
            {card.content.images.map((image, index) => (
              <img 
                key={index}
                src={getApiImageUrl(image.url)} 
                alt={`${getTranslation('cards.cardImage', 'Card image')} ${index + 1}`}
                className={`card-image ${image.position}`}
                onError={(e) => {
                  console.error(`Failed to load image: ${image.url}`);
                  e.target.style.display = 'none';
                }}
              />
            ))}
          </div>
        )}

        {/* Display question text */}
        <div className="question-text">
          {card.content.question && (
            <div className="question">
              {card.content.question.split('\n').map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
          )}
          
          {/* Handle lesson text format */}
          {card.content.text && (
            <div className="lesson-content">
              {card.content.text.map((paragraph, index) => (
                <div key={index} className="lesson-paragraph">
                  <p>{paragraph.paragraph}</p>
                  {paragraph.imageAfter && (
                    <img 
                      src={getApiImageUrl(`/api/images/${moduleId}/${paragraph.imageAfter}`)}
                      alt={`${getTranslation('cards.lessonImage', 'Lesson image')} ${index + 1}`}
                      className="lesson-image"
                      onError={(e) => {
                        console.error(`Failed to load lesson image: ${paragraph.imageAfter}`);
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Answer input area */}
        <form onSubmit={handleSubmit} className="answer-form">
          {card.type === 'lesson' ? (
            // Lesson - just show next button
            <button 
              type="submit" 
              className="next-button"
              disabled={submitting}
            >
              {submitting ? getTranslation('common.loading', 'Loading...') : getTranslation('cards.next', 'Next')}
            </button>
          ) : card.content.answerType === 'multiple_choice' ? (
            // Multiple choice
            <div className="multiple-choice">
              {card.content.options?.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={`choice-button ${selectedOption === option.key ? 'selected' : ''}`}
                  onClick={() => handleMultipleChoiceSelection(option.key)}
                  disabled={submitting}
                >
                  {option.value ? (
                    <>
                      <span className="choice-key">{option.key})</span>
                      <span className="choice-value">{option.value}</span>
                    </>
                  ) : (
                    <span className="choice-key-only">{option.key})</span>
                  )}
                </button>
              ))}
            </div>
          ) : (
            // Numerical or text input
            <div className="text-input">
              <input
                ref={inputRef}
                type="text"
                value={userAnswer}
                onChange={(e) => {
                  const value = e.target.value;
                  // For numerical answers, allow numbers, a single decimal
                  // separator (comma or period), and a leading minus sign.
                  if (card.content.answerType === 'numerical') {
                    if (value === '' || value === '-' || /^-?\d*[.,]?\d*$/.test(value)) {
                      setUserAnswer(value);
                    }
                  } else {
                    setUserAnswer(value);
                  }
                }}
                placeholder={getTranslation('cards.enterAnswer', 'Enter your answer')}
                className="answer-input"
                autoFocus
                inputMode={card.content.answerType === 'numerical' ? 'decimal' : 'text'}
              />
              <button 
                type="submit" 
                className="submit-button"
                disabled={!isAnswerValid() || submitting}
              >
                {submitting ? getTranslation('common.loading', 'Loading...') : getTranslation('cards.submit', 'Submit')}
              </button>
            </div>
          )}
        </form>
      </div>

    </div>
  );
};

export default CardScreen;